import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';

import { CenteredContainer, Title, Text, Link } from '@nerdwallet/currency';

import { useQuery, gql } from '@nerdwallet/apollo';
import PressCategory from './press-category/press-category';
import Hero from '../employee-branding/hero/hero';
import CompanyPeople from '../employee-branding/company-people/company-people';
import CompanyFooter from '../employee-branding/company-footer/company-footer';

import Spacer from '../spacer/spacer';

import CompanySpecialistContent from '../../lib/employee-branding/specialists';
import { logGqlError, logGqlInfo } from '~/client/lib/logger-utils';

const articleQuery = gql`
  query filteredPressReleases {
    pressReleases(
      filter: { featured: true }
      pagination: { limit: 4 }
      order: { field: DATE, sortDirection: DESC }
    ) {
      items {
        featuredMedia {
          sourceUrl
        }
        url
        title
        content
        mediaSites {
          logo {
            croppedImages(sizes: [FULL]) {
              sourceUrl
            }
          }
        }
      }
    }
  }
`;

const PressArchivePage = () => {
  const { data, loading, error } = useQuery(articleQuery);
  if (loading) {
    return null;
  }
  if (error) {
    logGqlError(error);
    return null;
  }
  if (!data?.pressReleases?.items) {
    logGqlInfo('No press releases available.');
    return null;
  }
  const articles = data.pressReleases.items;

  return (
    <div className="press-archive-page">
      <div
        className={classNames([
          classes('margin-bottom-4'),
          classes('margin-bottom-5--wide'),
        ])}
      >
        <Hero
          backgroundColor="#FFE8E8"
          title="NerdWallet in the news"
          copy="We're changing the finance world and making news along the way. Here's a look at some of our latest headlines."
          imageUrlMobile="https://www.nerdwallet.com/cdn/company/2021/hero/News-Hero-Mobile.jpg"
          imageUrlDesktop="https://www.nerdwallet.com/cdn/company/2021/hero/News-Hero-Desktop.jpg"
        />
      </div>
      <PressCategory articles={articles} displayLogoSalad />
      <CenteredContainer>
        <Title
          size="small"
          className={classNames([
            classes('margin-horizontal-auto'),
            classes('margin-bottom-3'),
            classes('margin-bottom-4--wide'),
            classes('text-align-center'),
          ])}
          component="h2"
        >
          Media inquiries
        </Title>
        <div
          className={classNames([
            classes('margin-horizontal-auto'),
            classes('margin-bottom-3'),
            classes('margin-bottom-4--wide'),
            classes('text-align-center'),
            classes('width-two-thirds--wide'),
          ])}
        >
          <Text>
            If you have specific inquiries for awards, please contact&nbsp;
            <Link href="mailto:awards@nerdwallet.com">
              awards@nerdwallet.com
            </Link>
            . For all other media inquiries, contact us below.
          </Text>
        </div>
        <div
          className={classNames([
            classes('margin-horizontal-auto'),
            classes('margin-bottom-3-half'),
            classes('margin-bottom-5--wide'),
            classes('text-align-center'),
            classes('width-two-thirds--wide'),
          ])}
        >
          <Text>
            <Link href="mailto:press@nerdwallet.com">press@nerdwallet.com</Link>
            &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;(415) 612–2534
          </Text>
        </div>
        <CenteredContainer>
          <hr
            className={classNames([
              classes('width-100'),
              classes('border-neutral-lighter'),
              classes('margin-bottom-3-half'),
              classes('margin-bottom-5--wide'),
            ])}
          />
        </CenteredContainer>
        <Title
          size="small"
          className={classNames([
            classes('margin-horizontal-auto'),
            classes('margin-bottom-3'),
            classes('margin-bottom-4--wide'),
            classes('text-align-center'),
          ])}
          component="h2"
        >
          Meet our spokespeople
        </Title>
        <div
          className={classNames([
            classes('margin-horizontal-auto'),
            classes('margin-bottom-3'),
            classes('margin-bottom-4--wide'),
            classes('text-align-center'),
            classes('width-two-thirds--wide'),
          ])}
        >
          <Text>
            Get to know some of our thought leaders and experienced pros.
          </Text>
        </div>
      </CenteredContainer>
      <CompanyPeople isSpecialist {...CompanySpecialistContent} />
      <Spacer ndsMarginClass="3" />
      <CompanyFooter
        backgroundColor="#FFE8E8"
        contrastColor="#FFE8E8"
        title="Looking for our press kit?"
        ctaText="download here"
        ctaUrl="https://www.nerdwallet.com/kit"
        imageUrlMobile="https://www.nerdwallet.com/cdn/company/2021/footer/mobile/Press-Kit-CTA-Illustration-Mobile_375.png"
        imageUrlDesktop="https://www.nerdwallet.com/cdn/company/2021/footer/desktop/Press-Kit-CTA-Illustration-Desktop.png"
        isSmall
      />
    </div>
  );
};

PressArchivePage.propTypes = {
  data: PropTypes.object.isRequired,
};

PressArchivePage.displayName = 'PressArchivePage';

export default PressArchivePage;
